import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { MdClose } from "react-icons/md"
import Template from "../Chat/Elements/Template"
import styles from "./whatsAppTemplateCard.module.css"

export const WhatsAppTemplatePreview = ({ template = {}, time = "12:00", className = "", children }) => {
    const [mediaSource, setMediaSource] = useState("")
    const [showMediaPreview, setShowMediaPreview] = useState(false)
    const content = template?.content || {}
    const mediaFormat = `${content?.header?.format || ""}`.toUpperCase()
    const canPreviewMedia = ["IMAGE", "VIDEO"].includes(mediaFormat) && Boolean(mediaSource)

    useEffect(() => {
        let objectUrl
        const file = content?.header?.file

        if (file instanceof Blob) {
            objectUrl = URL.createObjectURL(file)
            setMediaSource(objectUrl)
        } else {
            setMediaSource(content?.url || content?.urlThumb || content?.header?.handle || "")
        }

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [content?.header?.file, content?.header?.handle, content?.url, content?.urlThumb])

    useEffect(() => {
        if (!showMediaPreview) return undefined

        const previousOverflow = document.body.style.overflow
        const handleKeyDown = (event) => {
            if (event.key === "Escape") setShowMediaPreview(false)
        }
        document.body.style.overflow = "hidden"
        window.addEventListener("keydown", handleKeyDown)

        return () => {
            document.body.style.overflow = previousOverflow
            window.removeEventListener("keydown", handleKeyDown)
        }
    }, [showMediaPreview])

    useEffect(() => {
        if (!canPreviewMedia) setShowMediaPreview(false)
    }, [canPreviewMedia])

    const closeMediaPreview = (event) => {
        event?.preventDefault()
        event?.stopPropagation()
        setShowMediaPreview(false)
    }

    return (
        <div className={`${styles.preview} ${className}`}>
            <div className={styles.bubble}>
                <Template
                    content={content}
                    showPreview={true}
                    templatePreview={true}
                    openPreview={canPreviewMedia ? () => setShowMediaPreview(true) : undefined}
                />
                {children}
                <small>{time}</small>
            </div>
            {showMediaPreview && canPreviewMedia && createPortal(
                <div
                    className={styles.mediaLightbox}
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Visualização ampliada de ${mediaFormat === "VIDEO" ? "vídeo" : "imagem"}`}
                    onClick={closeMediaPreview}
                >
                    <button type="button" className={styles.mediaLightboxClose} aria-label="Fechar visualização" onClick={closeMediaPreview}>
                        <MdClose />
                    </button>
                    <div className={styles.mediaLightboxContent} onClick={(event) => event.stopPropagation()}>
                        {mediaFormat === "VIDEO"
                            ? <video src={mediaSource} controls autoPlay playsInline />
                            : <img src={mediaSource} alt="Mídia do cabeçalho do template" />
                        }
                    </div>
                </div>,
                document.body
            )}
        </div>
    )
}

const WhatsAppTemplateCard = ({
    template = {},
    title = "Template",
    header = null,
    meta = null,
    description = null,
    footer = null,
    time = "12:00",
    className = "",
    contentClassName = "",
    onClick,
}) => {
    const isInteractive = typeof onClick === "function"
    const handleKeyDown = (event) => {
        if (!isInteractive || !["Enter", " "].includes(event.key)) return
        event.preventDefault()
        onClick()
    }

    return (
        <article
            className={`${styles.card} ${className}`}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            role={isInteractive ? "button" : undefined}
            tabIndex={isInteractive ? 0 : undefined}
            aria-label={isInteractive ? `Selecionar template ${title}` : undefined}
        >
            {header}
            <WhatsAppTemplatePreview template={template} time={time} />
            <div className={`${styles.content} ${contentClassName}`}>
                <strong title={title}>{title}</strong>
                {meta}
                {description && <p>{description}</p>}
                {footer}
            </div>
        </article>
    )
}

export default WhatsAppTemplateCard
